import { useState, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Star, Trash2, EyeOff, Eye, Upload, GripVertical,
  ImagePlus, FolderOpen, Loader2, Tag,
} from 'lucide-react';
import R2BrowseDialog from './R2BrowseDialog';

const COLOR_TAGS = [
  { label: 'None', value: '', hex: 'transparent' },
  { label: 'Black', value: 'Black', hex: '#111' },
  { label: 'White', value: 'White', hex: '#f5f5f5' },
  { label: 'Red', value: 'Red', hex: '#dc2626' },
  { label: 'Blue', value: 'Blue', hex: '#2563eb' },
  { label: 'Green', value: 'Green', hex: '#16a34a' },
  { label: 'Yellow', value: 'Yellow', hex: '#eab308' },
  { label: 'Orange', value: 'Orange', hex: '#ea580c' },
  { label: 'Pink', value: 'Pink', hex: '#ec4899' },
  { label: 'Purple', value: 'Purple', hex: '#9333ea' },
  { label: 'Brown', value: 'Brown', hex: '#92400e' },
  { label: 'Grey', value: 'Grey', hex: '#6b7280' },
  { label: 'Navy', value: 'Navy', hex: '#1e3a5f' },
  { label: 'Beige', value: 'Beige', hex: '#d4b896' },
];

const SOURCE_LABELS = {
  printful: 'Printful',
  uploaded: 'Uploaded',
  r2_import: 'R2 Import',
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

function getThumbnailUrl(img) {
  if (img.path) return `${API_URL}/api/images/${img.path}/sm.webp`;
  return '';
}

export default function ProductGalleryManager({ product, onRefresh }) {
  const pid = product.printful_id;
  const gallery = (product.gallery || []).sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999));
  const useMockups = product.use_printful_mockups !== false;

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [editingAlt, setEditingAlt] = useState(null);
  const [editingColor, setEditingColor] = useState(null);
  const [showR2Browse, setShowR2Browse] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  // --- Upload ---
  const handleFiles = useCallback(async (files) => {
    const validFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!validFiles.length) { toast.error('Only image files accepted'); return; }
    const remaining = 12 - gallery.length;
    if (validFiles.length > remaining) {
      toast.error(`Only ${remaining} more image${remaining === 1 ? '' : 's'} allowed (max 12)`);
      return;
    }
    setUploading(true);
    let successCount = 0;
    for (const file of validFiles) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: exceeds 10MB limit`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      fd.append('color_tag', '');
      fd.append('alt_text', '');
      const fileKey = file.name;
      setUploadProgress(prev => ({ ...prev, [fileKey]: 0 }));
      try {
        await api.post(`/admin/gallery/${pid}/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setUploadProgress(prev => ({ ...prev, [fileKey]: Math.round((e.loaded / e.total) * 100) }));
          },
        });
        successCount++;
        setUploadProgress(prev => ({ ...prev, [fileKey]: 100 }));
      } catch (err) {
        const msg = err.response?.data?.detail || 'Upload failed';
        toast.error(`${file.name}: ${msg}`);
      }
    }
    setUploadProgress({});
    setUploading(false);
    if (successCount > 0) {
      toast.success(`${successCount} image${successCount === 1 ? '' : 's'} uploaded`);
      onRefresh();
    }
  }, [pid, gallery.length, onRefresh]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    dropZoneRef.current?.classList.remove('border-blue-500', 'bg-blue-950/30');
    const files = e.dataTransfer?.files;
    if (files?.length) handleFiles(files);
  }, [handleFiles]);

  const handleDragOver = (e) => { e.preventDefault(); dropZoneRef.current?.classList.add('border-blue-500', 'bg-blue-950/30'); };
  const handleDragLeave = () => { dropZoneRef.current?.classList.remove('border-blue-500', 'bg-blue-950/30'); };

  // --- Reorder via drag ---
  const handleReorderDragStart = (idx) => setDraggedIdx(idx);
  const handleReorderDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx); };
  const handleReorderDrop = async (e, dropIdx) => {
    e.preventDefault();
    setDragOverIdx(null);
    if (draggedIdx === null || draggedIdx === dropIdx) { setDraggedIdx(null); return; }
    const newOrder = gallery.map(g => g.id);
    const [moved] = newOrder.splice(draggedIdx, 1);
    newOrder.splice(dropIdx, 0, moved);
    setDraggedIdx(null);
    try {
      await api.put(`/admin/gallery/${pid}/reorder`, { order: newOrder });
      onRefresh();
    } catch { toast.error('Reorder failed'); }
  };
  const handleReorderDragEnd = () => { setDraggedIdx(null); setDragOverIdx(null); };

  // --- Actions ---
  const setCover = async (imageId) => {
    setSavingId(imageId);
    try { await api.put(`/admin/gallery/${pid}/${imageId}`, { is_cover: true }); onRefresh(); }
    catch { toast.error('Failed'); }
    finally { setSavingId(null); }
  };

  const toggleHidden = async (img) => {
    setSavingId(img.id);
    try { await api.put(`/admin/gallery/${pid}/${img.id}`, { hidden: !img.hidden }); onRefresh(); }
    catch { toast.error('Failed'); }
    finally { setSavingId(null); }
  };

  const deleteImage = async (img) => {
    setSavingId(img.id);
    try { await api.delete(`/admin/gallery/${pid}/${img.id}`); toast.success('Image deleted'); onRefresh(); }
    catch { toast.error('Delete failed'); }
    finally { setSavingId(null); }
  };

  const saveAlt = async (img, newAlt) => {
    setSavingId(img.id);
    try { await api.put(`/admin/gallery/${pid}/${img.id}`, { alt_text: newAlt }); onRefresh(); }
    catch { toast.error('Failed'); }
    finally { setSavingId(null); setEditingAlt(null); }
  };

  const saveColorTag = async (img, newTag) => {
    setSavingId(img.id);
    try { await api.put(`/admin/gallery/${pid}/${img.id}`, { color_tag: newTag }); onRefresh(); }
    catch { toast.error('Failed'); }
    finally { setSavingId(null); setEditingColor(null); }
  };

  const toggleMockups = async () => {
    try {
      await api.put(`/admin/shop/products/${pid}`, { use_printful_mockups: !useMockups });
      toast.success(useMockups ? 'Printful mockups disabled' : 'Printful mockups enabled');
      onRefresh();
    } catch { toast.error('Failed'); }
  };

  const handleR2Import = async (selectedKeys) => {
    let count = 0;
    for (const key of selectedKeys) {
      if (gallery.length + count >= 12) { toast.error('Gallery full (12 max)'); break; }
      try {
        await api.post(`/admin/gallery/${pid}/import-r2`, { key });
        count++;
      } catch (err) {
        toast.error(err.response?.data?.detail || `Import failed: ${key}`);
      }
    }
    if (count > 0) {
      toast.success(`${count} image${count === 1 ? '' : 's'} imported from R2`);
      onRefresh();
    }
    setShowR2Browse(false);
  };

  return (
    <div data-testid={`gallery-manager-${pid}`} className="space-y-3">
      {/* Header: counter + mockup toggle */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <span className="text-xs text-neutral-400 uppercase tracking-widest font-semibold">
            Gallery Images
          </span>
          <span data-testid={`gallery-counter-${pid}`} className={`text-xs font-mono px-2 py-0.5 rounded ${gallery.length >= 12 ? 'bg-red-950 text-red-400' : 'bg-neutral-800 text-neutral-400'}`}>
            {gallery.length} / 12
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs text-neutral-500 cursor-pointer">Use Printful mockups</Label>
          <Switch
            data-testid={`mockup-toggle-${pid}`}
            checked={useMockups}
            onCheckedChange={toggleMockups}
          />
        </div>
      </div>

      {/* Image grid */}
      {gallery.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {gallery.map((img, idx) => {
            const thumbUrl = getThumbnailUrl(img);
            const isCover = img.is_cover;
            const isHidden = img.hidden;
            const isSaving = savingId === img.id;
            const isDragging = draggedIdx === idx;
            const isDragTarget = dragOverIdx === idx;
            const sourceLabel = SOURCE_LABELS[img.source] || img.source || '?';
            const colorObj = COLOR_TAGS.find(c => c.value === img.color_tag);

            return (
              <div
                key={img.id}
                data-testid={`gallery-image-${img.id}`}
                draggable
                onDragStart={() => handleReorderDragStart(idx)}
                onDragOver={(e) => handleReorderDragOver(e, idx)}
                onDrop={(e) => handleReorderDrop(e, idx)}
                onDragEnd={handleReorderDragEnd}
                className={`relative group rounded border overflow-hidden transition-all ${
                  isDragging ? 'opacity-30 scale-95' : ''
                } ${isDragTarget ? 'border-blue-500 ring-2 ring-blue-500/30' : 'border-neutral-800'
                } ${isHidden ? 'opacity-50' : ''} ${isCover ? 'ring-2 ring-yellow-500/40' : ''}`}
              >
                {/* Thumbnail */}
                <div className="aspect-square bg-neutral-900 relative">
                  {thumbUrl && (
                    <img src={thumbUrl} alt={img.alt_text || ''} className="w-full h-full object-cover" loading="lazy" />
                  )}
                  {/* Drag handle */}
                  <div className="absolute top-1 left-1 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing bg-black/50 rounded p-0.5">
                    <GripVertical size={14} className="text-white/70" />
                  </div>
                  {/* Cover badge */}
                  {isCover && (
                    <div className="absolute top-1 right-1 bg-yellow-500 text-black text-[9px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wider">Cover</div>
                  )}
                  {/* Hidden overlay */}
                  {isHidden && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <EyeOff size={20} className="text-white/60" />
                    </div>
                  )}
                  {/* Saving spinner */}
                  {isSaving && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <Loader2 size={20} className="text-white animate-spin" />
                    </div>
                  )}
                </div>

                {/* Info bar */}
                <div className="bg-neutral-900 px-2 py-1.5 space-y-1">
                  {/* Source + color tag */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[10px] text-neutral-500 uppercase tracking-wider">{sourceLabel}</span>
                    {img.color_tag && colorObj && (
                      <span
                        className="inline-block w-3 h-3 rounded-full border border-neutral-600 flex-shrink-0"
                        style={{ backgroundColor: colorObj.hex }}
                        title={img.color_tag}
                      />
                    )}
                  </div>

                  {/* Alt text - click to edit */}
                  {editingAlt === img.id ? (
                    <AltEditor
                      initial={img.alt_text || ''}
                      onSave={(val) => saveAlt(img, val)}
                      onCancel={() => setEditingAlt(null)}
                    />
                  ) : (
                    <p
                      className="text-[10px] text-neutral-400 truncate cursor-pointer hover:text-neutral-200 transition-colors"
                      onClick={() => setEditingAlt(img.id)}
                      title="Click to edit alt text"
                    >
                      {img.alt_text || 'Add alt text...'}
                    </p>
                  )}

                  {/* Action buttons */}
                  <div className="flex items-center gap-0.5 -mx-1">
                    <button
                      data-testid={`set-cover-${img.id}`}
                      onClick={() => setCover(img.id)}
                      disabled={isCover || isSaving}
                      className={`p-1 rounded transition-colors ${isCover ? 'text-yellow-500 cursor-default' : 'text-neutral-600 hover:text-yellow-400'}`}
                      title="Set as cover"
                    >
                      <Star size={13} fill={isCover ? 'currentColor' : 'none'} />
                    </button>
                    <button
                      data-testid={`toggle-color-${img.id}`}
                      onClick={() => setEditingColor(editingColor === img.id ? null : img.id)}
                      className="p-1 rounded text-neutral-600 hover:text-purple-400 transition-colors"
                      title="Color tag"
                    >
                      <Tag size={13} />
                    </button>
                    <button
                      data-testid={`toggle-hidden-${img.id}`}
                      onClick={() => toggleHidden(img)}
                      disabled={isSaving}
                      className={`p-1 rounded transition-colors ${isHidden ? 'text-orange-400' : 'text-neutral-600 hover:text-neutral-300'}`}
                      title={isHidden ? 'Show image' : 'Hide image'}
                    >
                      {isHidden ? <Eye size={13} /> : <EyeOff size={13} />}
                    </button>
                    <button
                      data-testid={`delete-image-${img.id}`}
                      onClick={() => deleteImage(img)}
                      disabled={isSaving}
                      className="p-1 rounded text-neutral-600 hover:text-red-400 transition-colors"
                      title="Delete image"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>

                  {/* Color tag picker */}
                  {editingColor === img.id && (
                    <div className="flex flex-wrap gap-1 pt-1 border-t border-neutral-800">
                      {COLOR_TAGS.map(c => (
                        <button
                          key={c.value}
                          onClick={() => { saveColorTag(img, c.value); setEditingColor(null); }}
                          className={`w-5 h-5 rounded-full border-2 transition-all hover:scale-110 ${
                            img.color_tag === c.value ? 'border-white scale-110' : 'border-neutral-700'
                          }`}
                          style={{ backgroundColor: c.hex === 'transparent' ? 'transparent' : c.hex }}
                          title={c.label}
                        >
                          {c.value === '' && <span className="text-[8px] text-neutral-500 leading-none">X</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload progress */}
      {Object.keys(uploadProgress).length > 0 && (
        <div className="space-y-1">
          {Object.entries(uploadProgress).map(([name, pct]) => (
            <div key={name} className="flex items-center gap-2 text-xs text-neutral-400">
              <span className="truncate max-w-[200px]">{name}</span>
              <div className="flex-1 bg-neutral-800 rounded-full h-1.5">
                <div className="bg-blue-500 h-1.5 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <span className="font-mono w-8 text-right">{pct}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Drop zone + action buttons */}
      <div
        ref={dropZoneRef}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`border-2 border-dashed border-neutral-800 rounded-lg p-6 text-center transition-colors ${
          gallery.length >= 12 ? 'opacity-40 pointer-events-none' : 'hover:border-neutral-600'
        }`}
      >
        <div className="flex flex-col items-center gap-2">
          <ImagePlus size={24} className="text-neutral-600" />
          <p className="text-xs text-neutral-500">
            {gallery.length >= 12
              ? 'Gallery full (12 images max)'
              : 'Drag & drop images here, or use the buttons below'}
          </p>
          <p className="text-[10px] text-neutral-600">JPEG, PNG, WebP up to 10MB each</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          className="hidden"
          onChange={(e) => { if (e.target.files?.length) handleFiles(e.target.files); e.target.value = ''; }}
        />
        <Button
          data-testid={`upload-btn-${pid}`}
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || gallery.length >= 12}
          variant="outline"
          size="sm"
          className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"
        >
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {uploading ? 'Uploading...' : 'Upload Images'}
        </Button>
        <Button
          data-testid={`r2-browse-btn-${pid}`}
          onClick={() => setShowR2Browse(true)}
          disabled={uploading || gallery.length >= 12}
          variant="outline"
          size="sm"
          className="text-xs border-neutral-700 text-neutral-400 hover:text-white gap-1"
        >
          <FolderOpen size={14} />
          Import from R2
        </Button>
      </div>

      {/* R2 Browse Dialog */}
      {showR2Browse && (
        <R2BrowseDialog
          onImport={handleR2Import}
          onClose={() => setShowR2Browse(false)}
          maxSelect={12 - gallery.length}
        />
      )}
    </div>
  );
}

function AltEditor({ initial, onSave, onCancel }) {
  const [val, setVal] = useState(initial);
  const inputRef = useRef(null);
  return (
    <form
      onSubmit={(e) => { e.preventDefault(); onSave(val); }}
      className="flex gap-1"
    >
      <Input
        ref={inputRef}
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={() => onSave(val)}
        onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
        className="bg-neutral-800 border-neutral-700 text-white text-[10px] h-6 px-1.5"
        placeholder="Alt text..."
      />
    </form>
  );
}
